import {
  clampProgressPercent,
  formatProgressStepLabel,
  type OperationProgressModel,
  type OperationProgressStep,
} from '../../../shared/ui/progress';
import type { MappingParameterRepairKind } from './mappingProjectReadiness';

export interface ProjectScope {
  projectKey: string | null;
  modelVersion: number | null;
}

export type MappingRepairProgressStepId =
  | MappingParameterRepairKind
  | 'quickFill'
  | 'quickFillClassification'
  | 'connectorFinalization'
  | 'verify';

export interface ScopedRepairStepProgress {
  stepId: MappingRepairProgressStepId | null;
  model: OperationProgressModel;
}

export interface ActiveRepairProgressContext extends ProjectScope {
  signature: string;
  repairableCount: number;
  repairableParameterNames: string[];
  repairKinds: MappingParameterRepairKind[];
  repairParameterCounts: Record<MappingParameterRepairKind, number>;
  activeStepId: MappingRepairProgressStepId | null;
  completedStepIds: MappingRepairProgressStepId[];
  failedStepId: MappingRepairProgressStepId | null;
}

export function getRepairKindLabel(kind: MappingParameterRepairKind): string {
  switch (kind) {
    case 'connector':
      return 'Connector';
    case 'processProject':
      return 'Process/project';
    case 'additional':
      return 'Additional-category';
    case 'bolt':
      return 'Bolt properties';
    case 'mapping':
      return 'Mapped';
    default:
      return 'Project setup';
  }
}

function describeRepairKinds(repairKinds: MappingParameterRepairKind[]): string {
  const labels = repairKinds.map((kind) => {
    switch (kind) {
      case 'connector':
        return 'connector';
      case 'processProject':
        return 'process and project';
      case 'additional':
        return 'additional category';
      case 'bolt':
        return 'bolt';
      case 'mapping':
        return 'mapped';
      default:
        return kind;
    }
  });

  return labels.length > 0
    ? `Applying ${labels.join(', ')} setup parameters.`
    : 'Preparing mapping setup.';
}

const MappingRepairKindOrder: MappingParameterRepairKind[] = [
  'connector',
  'bolt',
  'additional',
  'processProject',
  'mapping',
];

export const emptyRepairParameterCounts: Record<MappingParameterRepairKind, number> = {
  connector: 0,
  processProject: 0,
  bolt: 0,
  additional: 0,
  mapping: 0,
};

export function sortRepairKinds(repairKinds: MappingParameterRepairKind[]): MappingParameterRepairKind[] {
  return [...repairKinds].sort(
    (left, right) =>
      MappingRepairKindOrder.indexOf(left) - MappingRepairKindOrder.indexOf(right)
  );
}

export function isParameterAdditionRepairStepId(
  stepId: MappingRepairProgressStepId | null
): stepId is Exclude<MappingParameterRepairKind, 'bolt'> {
  return stepId === 'connector' || stepId === 'processProject' || stepId === 'additional' || stepId === 'mapping';
}

export function doesParameterProgressMatchRepairStep(
  stepId: MappingRepairProgressStepId | null,
  progress: OperationProgressModel | null
): boolean {
  if (!isParameterAdditionRepairStepId(stepId) || !progress) {
    return false;
  }

  const progressText = `${progress.title ?? ''} ${progress.message ?? ''}`.toLowerCase();
  switch (stepId) {
    case 'connector':
      return progressText.includes('connector');
    case 'processProject':
      return progressText.includes('process') || progressText.includes('project');
    case 'additional':
      return progressText.includes('category') || progressText.includes('additional');
    case 'mapping':
      return progressText.includes('mapped');
    default:
      return false;
  }
}

export function buildRepairStepIds(repairKinds: MappingParameterRepairKind[]): MappingRepairProgressStepId[] {
  const orderedKinds = sortRepairKinds(repairKinds);
  const steps: MappingRepairProgressStepId[] = [];

  if (orderedKinds.includes('connector')) {
    steps.push('connector');
  }

  // The classification primer (SKEY/OBJECT-TYPE) runs before bolt/additional
  // parameter creation; the authoritative Quick Fill autofill runs last, once
  // every setup step has created its parameters.
  if (orderedKinds.includes('bolt') || orderedKinds.includes('additional')) {
    steps.push('quickFillClassification');
  }

  if (orderedKinds.includes('bolt')) {
    steps.push('bolt');
  }

  if (orderedKinds.includes('additional')) {
    steps.push('additional');
  }

  // Bolt setup can load a GASKET family and additional-category setup can
  // expose new editable target families. Those families did not exist during
  // the first connector pass, so they need one targeted connector pass before
  // Quick Fill and verification.
  if (
    orderedKinds.includes('connector') &&
    (orderedKinds.includes('bolt') || orderedKinds.includes('additional'))
  ) {
    steps.push('connectorFinalization');
  }

  if (orderedKinds.includes('processProject')) {
    steps.push('processProject');
  }

  if (orderedKinds.includes('mapping')) {
    steps.push('mapping');
  }

  if (
    orderedKinds.includes('mapping') ||
    orderedKinds.includes('bolt') ||
    orderedKinds.includes('additional')
  ) {
    steps.push('quickFill');
  }

  steps.push('verify');
  return steps;
}

function formatSetupParameterCount(count: number): string {
  return `${count} setup parameter${count === 1 ? '' : 's'}`;
}

function getRepairStepLabel(stepId: MappingRepairProgressStepId): string {
  switch (stepId) {
    case 'connector':
      return 'Connector setup';
    case 'processProject':
      return 'Process and project setup';
    case 'quickFill':
      return 'Apply Quick Fill setup';
    case 'quickFillClassification':
      return 'Quick Fill classification';
    case 'connectorFinalization':
      return 'Finalize family setup';
    case 'bolt':
      return 'Bolt setup';
    case 'additional':
      return 'Additional-category setup';
    case 'mapping':
      return 'Mapped parameters';
    case 'verify':
      return 'Check project parameters';
    default:
      return 'Project setup';
  }
}

function getRepairStepParameterCount(
  stepId: MappingRepairProgressStepId,
  counts: Record<MappingParameterRepairKind, number>
): number {
  if (stepId === 'quickFill') {
    return counts.bolt + counts.additional;
  }

  if (stepId === 'quickFillClassification') {
    // The primer only writes classification values for the same families; it
    // owns no setup parameters of its own.
    return 0;
  }

  if (stepId === 'connectorFinalization') {
    return counts.connector;
  }

  if (stepId === 'verify') {
    return (
      (counts.connector ?? 0) +
      (counts.processProject ?? 0) +
      (counts.bolt ?? 0) +
      (counts.additional ?? 0) +
      (counts.mapping ?? 0)
    );
  }

  return counts[stepId] ?? 0;
}

function buildRepairStepDetail(
  stepId: MappingRepairProgressStepId,
  context: ActiveRepairProgressContext
): string {
  const count = getRepairStepParameterCount(stepId, context.repairParameterCounts);
  const countDetail = formatSetupParameterCount(count);

  switch (stepId) {
    case 'connector':
      return `Connector setup; ${countDetail} affected.`;
    case 'processProject':
      return `Process and project setup; ${countDetail} affected.`;
    case 'quickFill':
      return `Quick Fill setup values; ${countDetail} affected before family setup.`;
    case 'quickFillClassification':
      return 'SKEY/OBJECT-TYPE classification values for family setup.';
    case 'bolt':
      return `Selected bolt-property setup; ${countDetail} affected.`;
    case 'additional':
      return `Additional-category setup; ${countDetail} affected.`;
    case 'connectorFinalization':
      return `Connector setup for families introduced during setup; ${countDetail} checked.`;
    case 'mapping':
      return `Mapped project parameters; ${countDetail} affected.`;
    case 'verify':
      return `Rechecking ${formatSetupParameterCount(context.repairableCount)} in the active project.`;
    default:
      return countDetail;
  }
}

function buildRepairProgressSteps(context: ActiveRepairProgressContext): OperationProgressStep[] {
  return buildRepairStepIds(context.repairKinds).map((stepId) => {
    let status: OperationProgressStep['status'] = 'pending';
    if (context.failedStepId === stepId) {
      status = 'error';
    } else if (context.completedStepIds.includes(stepId)) {
      status = 'complete';
    } else if (context.activeStepId === stepId) {
      status = 'current';
    }

    return {
      id: stepId,
      label: getRepairStepLabel(stepId),
      detail: buildRepairStepDetail(stepId, context),
      status,
    };
  });
}

function getRepairStepWeight(
  stepId: MappingRepairProgressStepId,
  counts: Record<MappingParameterRepairKind, number>
): number {
  // The verify pass re-inspects the whole project, so it costs as much as all
  // repair steps combined; counting it as a single step pinned the bar near
  // 100% while the longest phase was still running.
  return Math.max(1, getRepairStepParameterCount(stepId, counts));
}

function buildAggregateRepairPercent(
  steps: OperationProgressStep[],
  activeStepId: MappingRepairProgressStepId | null,
  childPercent: number | null | undefined,
  repairParameterCounts: Record<MappingParameterRepairKind, number>
): number | null {
  if (steps.length === 0) {
    return null;
  }

  const totalWeight = steps.reduce(
    (sum, step) => sum + getRepairStepWeight(step.id as MappingRepairProgressStepId, repairParameterCounts),
    0
  );
  const completedWeight = steps
    .filter((step) => step.status === 'complete')
    .reduce(
      (sum, step) => sum + getRepairStepWeight(step.id as MappingRepairProgressStepId, repairParameterCounts),
      0
    );
  const activeStep = activeStepId
    ? steps.find((step) => step.id === activeStepId)
    : null;
  const childFraction =
    activeStep && activeStep.status === 'current'
      ? (clampProgressPercent(childPercent) ?? 0) / 100
      : 0;
  const activeWeight = activeStep
    ? getRepairStepWeight(activeStep.id as MappingRepairProgressStepId, repairParameterCounts)
    : 0;

  return Math.round(((completedWeight + childFraction * activeWeight) / totalWeight) * 100);
}

function buildRepairProgressMessage(
  context: ActiveRepairProgressContext,
  childProgress: OperationProgressModel | null
): string {
  if (childProgress?.message && childProgress.status !== 'complete') {
    return childProgress.message;
  }

  if (context.activeStepId === 'verify') {
    return 'Revit is checking project parameters after setup changes';
  }

  return context.repairableCount > 0
    ? `Revit is adding ${formatSetupParameterCount(context.repairableCount)}`
    : 'Revit is preparing missing setup parameters';
}

export function buildMappingRepairAggregateProgress(input: {
  context: ActiveRepairProgressContext;
  childProgress: OperationProgressModel | null;
  fallbackProgress: OperationProgressModel | null;
}): OperationProgressModel {
  const { context, childProgress, fallbackProgress } = input;
  const steps = buildRepairProgressSteps(context);
  const allStepsComplete = steps.length > 0 && steps.every((step) => step.status === 'complete');
  const childCompleteDuringHandoff =
    childProgress?.status === 'complete' && !allStepsComplete;
  const visibleChildProgress = childProgress;
  const aggregateChildPercent =
    childCompleteDuringHandoff && context.completedStepIds.length > 0
      ? null
      : visibleChildProgress?.percent;
  const activeStepIndex = context.activeStepId
    ? steps.findIndex((step) => step.id === context.activeStepId)
    : -1;
  const failed = Boolean(context.failedStepId || childProgress?.status === 'error');
  const status: OperationProgressModel['status'] = failed
    ? 'error'
    : childProgress?.status === 'waiting'
      ? 'waiting'
      : childProgress?.status === 'complete' && allStepsComplete
        ? 'complete'
        : 'running';
  const detailParts = [
    visibleChildProgress?.detail,
    visibleChildProgress?.currentItem,
    fallbackProgress?.detail,
    describeRepairKinds(context.repairKinds),
  ].filter((value): value is string => Boolean(value && value.trim()));

  return {
    ...visibleChildProgress,
    operationId: `mapping-readiness-repair:${context.signature}`,
    title: 'Creating missing Revit parameters',
    message: buildRepairProgressMessage(context, visibleChildProgress),
    detail: detailParts[0],
    status,
    state: childCompleteDuringHandoff ? 'running' : visibleChildProgress?.state,
    resultReady: childCompleteDuringHandoff ? false : visibleChildProgress?.resultReady,
    percent: buildAggregateRepairPercent(
      steps,
      context.activeStepId,
      aggregateChildPercent,
      context.repairParameterCounts
    ),
    stepLabel:
      activeStepIndex >= 0
        ? formatProgressStepLabel(activeStepIndex + 1, steps.length, 1)
        : childProgress?.stepLabel,
    steps,
    metrics: [
      {
        label: 'Missing parameters',
        value: String(context.repairableCount),
        tone: context.repairableCount > 0 ? 'warning' : 'default',
      },
      {
        label: 'Setup areas',
        value: context.repairKinds.map(getRepairKindLabel).join(', ') || 'Project setup',
        tone: 'info',
      },
      ...(visibleChildProgress?.metrics ?? []),
    ],
    locked: status !== 'error',
  };
}

export function getNextRepairProgressStepId(
  current: ActiveRepairProgressContext,
  completedStepIds: MappingRepairProgressStepId[],
  completedStepId: MappingRepairProgressStepId
): MappingRepairProgressStepId {
  return buildRepairStepIds(current.repairKinds).find(
    (candidate) =>
      candidate !== completedStepId &&
      !completedStepIds.includes(candidate)
  ) ?? completedStepId;
}

export function buildMappingRepairFallbackProgress(input: {
  repairableCount: number;
  repairKinds: MappingParameterRepairKind[];
}): OperationProgressModel {
  return {
    title: 'Creating missing Revit parameters',
    message:
      input.repairableCount > 0
        ? `Revit is adding ${input.repairableCount} missing setup parameter${input.repairableCount === 1 ? '' : 's'}`
        : 'Revit is preparing missing setup parameters',
    detail: describeRepairKinds(input.repairKinds),
    status: 'running',
    percent: null,
    locked: true,
  };
}

export function createActiveRepairProgressContext(input: {
  signature: string;
  projectKey: string | null;
  modelVersion: number | null;
  repairableCount: number;
  repairableParameterNames: string[];
  repairKinds: MappingParameterRepairKind[];
  repairParameterCounts: Record<MappingParameterRepairKind, number>;
}): ActiveRepairProgressContext {
  const repairKinds = sortRepairKinds(input.repairKinds);

  return {
    signature: input.signature,
    projectKey: input.projectKey,
    modelVersion: input.modelVersion,
    repairableCount: input.repairableCount,
    repairableParameterNames: [...input.repairableParameterNames],
    repairKinds,
    repairParameterCounts: input.repairParameterCounts,
    activeStepId: buildRepairStepIds(repairKinds)[0] ?? null,
    completedStepIds: [],
    failedStepId: null,
  };
}

export function buildQuickFillRepairProgressModel(stage: {
  title: string;
  message: string;
  detail?: string;
}): OperationProgressModel {
  return {
    title: stage.title,
    message: stage.message,
    detail: stage.detail,
    status: 'running',
    percent: null,
    locked: true,
  };
}
